import React from 'react';
import { Handle, Position } from 'reactflow';
import type { LlmRoundData } from '../types';
import { useTheme } from '../../../../contexts/ThemeContext';
import { getPaletteEntry } from '../../statusPalette';
import { usePersonaChatUI } from '../../../../contexts/PersonaChatUIContext';
import { useDagNodeOpacity } from '../../../../contexts/ChatDagRuntimeContext';
import HoverCard from '../../../common/HoverCard';
import DagNodeHoverContent from './DagNodeHoverContent';
import { MarkdownPreview } from '../MarkdownPreview';
import { CopyChip } from '../CopyChip';
import {
  PIP_HEADER_CLASS,
  HOVER_LIFT_CLASS,
  SELECTION_RING_CLASS,
} from '../chatDagTokens';

// Per-round LLM call.
//
// UX triad review (v0.2): model name is the single most useful fact
// for an operator debugging cost, so it's now the dominant line
// instead of a truncated subtext. Round # becomes a small pip.
//
// Synthesis rounds (last round of a turn) get an indigo accent +
// ⚡ glyph so "where the persona wrote the answer" is spottable at
// a glance.
//
// v0.3 runState: inert for persisted rounds (runState === 'completed').
// When the live in-flight pipeline (Task 11-12) flips a round to
// 'running', the wrapper adds a pulsing emerald ring + an elapsed
// timer pill. Error-like states inherit the statusPalette border.

function shortenModel(modelId?: string): string | null {
  if (!modelId) return null;
  // Drop provider prefix + date suffix: `anthropic/claude-sonnet-4-5-20250929`
  //                                    → `claude-sonnet-4-5`
  const tail = modelId.split('/').pop() ?? modelId;
  const noDate = tail.replace(/-\d{8}$/, '');
  return noDate;
}

function fmtTokens(n?: number | null): string | null {
  if (n == null || n <= 0) return null;
  if (n < 1000) return `${n} tok`;
  return `${(n / 1000).toFixed(1)}k tok`;
}

function fmtCost(usd?: number | null): string | null {
  if (usd == null || usd <= 0) return null;
  if (usd < 0.001) return `$${usd.toFixed(5)}`;
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  if (usd < 1) return `$${usd.toFixed(3)}`;
  return `$${usd.toFixed(2)}`;
}

// Cost-tier color: amber over 5¢, rose over $1. Empty string keeps
// the inherited indigo on the synth meta line. Operator FEELS an
// expensive turn instead of having to read every digit.
function costTierClass(usd: number | null | undefined, darkMode: boolean): string {
  if (usd == null) return '';
  if (usd >= 1) return darkMode ? 'text-rose-400 font-semibold' : 'text-rose-600 font-semibold';
  if (usd >= 0.05) return darkMode ? 'text-amber-300' : 'text-amber-600';
  return '';
}

function fmtDuration(ms?: number | null): string | null {
  if (ms == null || ms <= 0) return null;
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function useElapsed(startMs: number | null): number {
  const [elapsed, setElapsed] = React.useState(0);
  React.useEffect(() => {
    if (startMs == null) return;
    setElapsed(Math.max(0, Math.floor((Date.now() - startMs) / 1000)));
    const t = window.setInterval(() => {
      setElapsed(Math.max(0, Math.floor((Date.now() - startMs) / 1000)));
    }, 1000);
    return () => window.clearInterval(t);
  }, [startMs]);
  return elapsed;
}

export const LlmRoundNodeCompact: React.FC<{ data: LlmRoundData; selected?: boolean }> = ({ data, selected }) => {
  const { darkMode } = useTheme();
  const { focusBubble } = usePersonaChatUI();
  const synth = data.isSynthesis;
  const messageId = data.messageId;
  const onClick = messageId
    ? (e: React.MouseEvent) => {
        e.stopPropagation();
        focusBubble(messageId);
      }
    : undefined;
  const runState = data.runState ?? 'completed';
  const running = runState === 'running';
  const errorLike =
    runState === 'error' ||
    runState === 'timeout' ||
    runState === 'rate_limited' ||
    runState === 'budget_exceeded';

  const accent = errorLike
    ? getPaletteEntry(runState).bgClass +
      ' border-2 ' +
      (darkMode
        ? 'border-rose-500/70 text-rose-100'
        : 'border-rose-500 text-rose-900')
    : synth
      ? darkMode
        ? 'bg-indigo-700/25 border-indigo-500/70 text-indigo-50'
        : 'bg-indigo-50 border-indigo-500 text-indigo-900'
      : darkMode
        ? 'bg-violet-700/25 border-violet-500/70 text-violet-50'
        : 'bg-violet-50 border-violet-500 text-violet-900';

  const pipStyle = synth
    ? darkMode
      ? 'text-indigo-300/80'
      : 'text-indigo-600'
    : darkMode
      ? 'text-violet-300/80'
      : 'text-violet-600';

  const modelShort = shortenModel(data.modelUsed);

  // startMs can be carried on the runtime projection (Task 12); if
  // absent on a running node, fall back to now() so the counter
  // starts fresh rather than jumping negative.
  const startMs = running ? ((data as any).startMs ?? Date.now()) : null;
  const elapsed = useElapsed(startMs);
  const mm = String(Math.floor(elapsed / 60)).padStart(2, '0');
  const ss = String(elapsed % 60).padStart(2, '0');
  const opacityStyle = useDagNodeOpacity(data.turnKey, running);

  // Synthesis turn-meta line: tokens · cost · duration. Only shown for
  // completed synthesis rounds where the asst message has landed and
  // the processor populated turnTotal* fields. Each chunk is optional.
  const synthTokens = synth ? fmtTokens(data.turnTotalTokens) : null;
  const synthCost = synth ? fmtCost(data.turnTotalCostUsd) : null;
  const synthDuration = synth ? fmtDuration(data.turnDurationMs) : null;
  const synthCostTier = synth ? costTierClass(data.turnTotalCostUsd, darkMode) : '';
  const hasSynthMeta = !running && (synthTokens || synthCost || synthDuration);
  const hasSynthAnswer = !running && synth && !!data.turnAnswerPreview;

  // T3 — the synth-landed beat. When a synthesis round transitions
  // from running → completed, paint a one-shot indigo ring-flare for
  // ~700ms so the user feels the answer arrive as a physical pulse,
  // not a quiet text swap. Tracks prev runState via ref so we only
  // fire on the actual transition (not on any re-render).
  const prevRunStateRef = React.useRef<typeof runState>(runState);
  const [justLanded, setJustLanded] = React.useState(false);
  React.useEffect(() => {
    const prev = prevRunStateRef.current;
    if (synth && prev === 'running' && runState === 'completed') {
      setJustLanded(true);
      const t = window.setTimeout(() => setJustLanded(false), 750);
      prevRunStateRef.current = runState;
      return () => window.clearTimeout(t);
    }
    prevRunStateRef.current = runState;
  }, [synth, runState]);

  // Synthesis nodes lift wider so the "delivery" surface can carry
  // meta + markdown answer preview without clipping. Non-synth nodes
  // also lifted slightly (200 → 220) to fit a comma-separated tool
  // list when calls fan out.
  const cardWidth = synth ? 'w-[240px]' : 'w-[220px]';

  // Compact tool-list line for non-synth rounds — the at-a-glance
  // "what tools fanned out from this round" answer. Truncates after
  // a small budget so the card height stays bounded; full list lives
  // in the hover (and the actual tool nodes hang off the right edge).
  const TOOL_NAMES_BUDGET = 3;
  const toolNames = data.toolNames ?? [];
  const visibleToolNames = toolNames.slice(0, TOOL_NAMES_BUDGET);
  const overflowToolCount = Math.max(0, toolNames.length - TOOL_NAMES_BUDGET);
  const hasToolList = !synth && visibleToolNames.length > 0;

  return (
    <HoverCard
      maxWidth={360}
      content={<DagNodeHoverContent data={data} />}
    >
    <div
      className={`group relative px-2.5 py-2 rounded-md border ${cardWidth} shadow-sm ${HOVER_LIFT_CLASS} ${accent} ${
        running ? 'chatdag-running-ring' : ''
      } ${justLanded ? 'chatdag-synth-landed' : ''} ${selected ? SELECTION_RING_CLASS : ''} ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      title={data.modelUsed ?? ''}
      style={opacityStyle}
    >
      {/* Spine-in from prev round (or user) */}
      <Handle
        type="target"
        position={Position.Top}
        id="t"
        className="!opacity-0 !pointer-events-none"
      />

      {synth && data.turnAnswerPreview && (
        <CopyChip text={data.turnAnswerPreview} label="Copy answer" />
      )}
      {/* Branch-out to tools on the right */}
      <Handle
        type="source"
        position={Position.Right}
        id="r"
        className="!opacity-0 !pointer-events-none"
      />

      {/* Synth keeps the pip-led layout because ⚡ Synthesis + the
          ✓ delivered badge IS the differentiator at a glance. Non-
          synth rounds put MODEL on top because the model is what
          actually distinguishes one round from another (the file's
          v0.2 doctrine: "DIFFERENTIATING field is dominant"). */}
      {synth ? (
        <>
          <div className={`flex items-center gap-1.5 ${PIP_HEADER_CLASS} ${pipStyle}`}>
            <span>⚡ Synthesis</span>
            {!running && !errorLike && (
              <span className={`chatdag-badge-pop ml-1 normal-case tracking-normal text-[9px] font-semibold ${
                darkMode ? 'text-emerald-300/80' : 'text-emerald-600'
              }`}>
                ✓ delivered
              </span>
            )}
            <span className="ml-auto opacity-60 font-mono normal-case tracking-normal">#{data.round}</span>
          </div>
          <div className="mt-1 font-semibold text-xs font-mono truncate">
            {modelShort ?? (running ? (
              <span className="opacity-75 italic">⏳ thinking…</span>
            ) : (
              <span className="opacity-40">—</span>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="font-semibold text-xs font-mono flex items-center gap-1.5">
            <span className="flex-1 truncate">
              {modelShort ?? (running ? (
                <span className="opacity-75 italic">⏳ thinking…</span>
              ) : (
                <span className="opacity-40">—</span>
              ))}
            </span>
            <span className="opacity-60 font-mono text-[10px] shrink-0 normal-case tracking-normal">
              #{data.round}
            </span>
          </div>
          <div className={`mt-0.5 ${PIP_HEADER_CLASS} opacity-70 ${pipStyle}`}>
            {data.isLoneAnswer ? '⚙ Answer' : '⚙ LLM Round'}
          </div>
        </>
      )}

      {/* Fan-out: prefer the tool-name list (more useful at a glance)
          when we have it; fall back to the bare count for legacy /
          mid-flight cases. Synthesis rounds with toolCallCount > 0
          (rare — synthesis usually has zero tools) still get the
          count so the visual flow is consistent. */}
      {hasToolList && (
        <div
          className={`mt-0.5 text-[10px] truncate font-mono ${
            darkMode ? 'opacity-80' : 'opacity-75'
          }`}
          title={toolNames.join(', ')}
        >
          <span className="opacity-60 not-italic">→ </span>
          {visibleToolNames.join(' · ')}
          {overflowToolCount > 0 && (
            <span className="opacity-60"> · +{overflowToolCount}</span>
          )}
        </div>
      )}
      {!hasToolList && data.toolCallCount > 0 && (
        <div className="mt-0.5 text-[10px] opacity-70">
          → {data.toolCallCount} {data.toolCallCount === 1 ? 'tool' : 'tools'} fanning out
        </div>
      )}
      {data.toolCallCount === 0 && !synth && !running && (
        <div className="mt-0.5 text-[10px] opacity-60 italic">no tools this round</div>
      )}
      {/* Synthesis status: ONLY while running. Used to render
          "writing the answer" forever — even after the answer landed —
          which made completed synthesis nodes look perpetually
          in-progress. Now only shows during the actual writing window. */}
      {synth && running && (
        <div className="mt-0.5 text-[10px] opacity-70 italic">⏳ writing the answer</div>
      )}

      {/* Synthesis delivery card — turn meta line + answer preview.
          Renders only for completed synthesis rounds (turn telemetry
          + content come from the assistant message metadata). The
          synthesis round becomes the "this is where the answer
          landed" landmark on the trail without forcing the user to
          drop down to the assistant marker for the totals. */}
      {hasSynthMeta && (
        <div className={`mt-1 text-[10px] font-mono flex items-center gap-2 flex-wrap ${
          darkMode ? 'text-indigo-200/80' : 'text-indigo-700/85'
        }`}>
          {synthDuration && <span>⏱ {synthDuration}</span>}
          {synthTokens && <span>{synthTokens}</span>}
          {synthCost && <span className={synthCostTier}>💰 {synthCost}</span>}
        </div>
      )}
      {hasSynthAnswer && (
        <div
          className={`mt-1 ${
            darkMode ? 'text-indigo-100/90' : 'text-indigo-900/90'
          }`}
          title={data.turnAnswerPreview}
        >
          <div className={`opacity-60 mb-0.5 ${PIP_HEADER_CLASS}`}>
            ↓ answer
          </div>
          <MarkdownPreview
            text={data.turnAnswerPreview ?? ''}
            maxLines={5}
            textSizeClass="text-[11px]"
          />
        </div>
      )}

      {/* Running pill — top-right corner, above the shadow ring */}
      {running && (
        <div
          className="absolute -top-2 right-2 text-[9px] font-mono bg-emerald-600 text-white px-1.5 py-0.5 rounded-full opacity-90 shadow"
          aria-live="polite"
        >
          ⏳ {mm}:{ss}
        </div>
      )}

      {/* Error-like status chip */}
      {errorLike && (
        <div className="mt-1 text-[10px] font-mono opacity-90">
          {getPaletteEntry(runState).icon} {getPaletteEntry(runState).label ?? runState}
        </div>
      )}

      {/* Spine-out to next round or asst */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="b"
        className="!opacity-0 !pointer-events-none"
      />
    </div>
    </HoverCard>
  );
};

export default LlmRoundNodeCompact;
